import { z } from 'zod';
import { zodToJsonSchema } from 'zod-to-json-schema';
import { E164, LocalDate } from '../primitives.js';
import { CallerMessageKind, UnmetRequestKind } from '../enums.js';
import { ChangeFailureReason } from '../schemas/booking.js';
import { MAX_SERVICE_QUESTIONS } from '../schemas/serviceQuestions.js';
import { checkCustomerName, CustomerNameSchema } from '../schemas/customerName.js';
/**
 * ## `.nullish()`, not `.optional()`, on every optional argument
 *
 * A model asked to "omit" a field routinely sends `null` instead, and zod
 * treats `null` and `undefined` as different things — so `.optional()` rejects
 * the whole tool call. It shipped that way and cost a real conversation:
 * `find_available_slots` with `staff_id: null` came back `invalid_arguments`,
 * the assistant reported it to the caller as "no free times", then guessed
 * `staff_id: "default"`, which worked — so the caller was told a slot was both
 * taken and free within a minute — and the closing `book_appointment` failed
 * the same way. `customer_phone` was already `.nullish()` for exactly this
 * reason; the lesson had been learnt in one field and nowhere else.
 *
 * Every optional argument here is therefore `.nullish()`, and each call site
 * normalises `null` to "not given".
 *
 * AI function-calling contract. These zod schemas are the single source of
 * truth: the gateway validates the model's tool arguments with them (ADR-0007
 * step 1), and the OpenAI session's tool JSON-schema is *generated* from them
 * (`buildOpenAiTools`) so the model is told exactly the contract the server
 * enforces (ADR-0008 rule 2).
 */
// ── Tool argument schemas ────────────────────────────────────────────────────
/**
 * Answers to a service's questions (doc 64), as a model hands them in. Only the
 * id and the answer: the question text saved with the booking is always the
 * service's own, never the model's.
 */
export const ToolServiceAnswer = z.object({
    question_id: z.string().min(1).max(64).describe('The id of the question, exactly as listed with the service.'),
    answer: z
        .string()
        .describe("The caller's answer: short and faithful, in the BUSINESS's language, keeping numbers, names and model names exactly as said. Never invented, never a placeholder."),
});
const ServiceAnswersArg = z
    .array(ToolServiceAnswer)
    .max(MAX_SERVICE_QUESTIONS * 2)
    .nullish();
/** Tool answers as the domain takes them. */
export function answerInputsFromTool(answers) {
    return (answers ?? []).map((a) => ({ questionId: a.question_id, answer: a.answer }));
}
export const FindAvailableSlotsArgs = z.object({
    service_id: z.string().min(1).describe('The id of the service the caller wants.'),
    date: LocalDate.describe('Date in YYYY-MM-DD, in the business timezone.'),
    staff_id: z
        .string()
        .nullish()
        .describe('Only when the caller asked for a specific staff member; otherwise omit.'),
    from_time: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .nullish()
        .describe('HH:mm. Only to see bookable starts at or after this time — e.g. the caller wants "after 17:00", or a previous result said more starts follow ("next_from"). Omit otherwise.'),
});
/**
 * `prepare_booking` (doc 33 W6): the details of ONE appointment the caller
 * wants, handed to the server BEFORE the read-back. The server validates them,
 * records a server-owned booking intent (id + version), and returns the exact
 * words to read back. Nothing is booked.
 *
 * Why two tools: a model-supplied `confirmed: true` is not evidence of
 * anything, and neither is a fresh id the model makes up. The intent id comes
 * from the server; `book_appointment` then commits it only if the caller took a
 * turn AFTER the read-back of that exact version (doc 32 F11).
 */
export const PrepareBookingArgs = z.object({
    service_id: z.string().min(1),
    start: z
        .string()
        .describe('ISO 8601 start with the business timezone offset, e.g. 2026-07-17T10:30:00+02:00.'),
    customer_name: CustomerNameSchema.describe("The first name the caller gave for this booking, exactly as they said it, in Latin letters — romanised the way their own language does it (Андрій → Andrii, Андрэй → Andrei), never translated into another language's form of the name. Never invent it, never use the business's or a staff member's name, never a placeholder such as \"Client\" or \"Unknown\": if you do not have it, ask the caller."),
    customer_phone: E164.nullish().describe('E.164 callback number. Defaults to caller ID; REQUIRED when caller ID is withheld (CLIR).'),
    staff_id: z
        .string()
        .nullish()
        .describe('Only when the caller asked for a specific staff member; otherwise omit and the server assigns one.'),
    note: z
        .string()
        .max(300)
        .nullish()
        .describe('One short line of anything the caller said that the other fields cannot hold and the staff would want to know — e.g. "for the spaniel, Rex", "may be 10 minutes late", "same colour as last time". Omit when there is nothing. Never put the name, phone number, service or time in here: they have their own fields.'),
    service_answers: ServiceAnswersArg.describe("The caller's answer to EVERY question listed with this service, one entry per question id. Required when the service has questions; omit for a service without any. Never put answers in note."),
});
/** `book_appointment` (doc 33 W6): commit ONE prepared intent, by the id
 * `prepare_booking` returned. */
export const BookAppointmentArgs = z.object({
    intent_id: z
        .string()
        .min(1)
        .describe('The intent_id prepare_booking returned for the details the caller has just said yes to.'),
});
export const EndCallArgs = z.object({});
export const LookupClientArgs = z.object({
    phone: E164.describe('The phone number the caller stated, normalised to E.164 (e.g. +33612345678).'),
});
/**
 * `note_unmet_request` (doc 23 P0) — the assistant reporting a dead end.
 *
 * Recording only: it changes nothing about the call and returns no information
 * the model can act on. That is deliberate. A tool that answered would tempt
 * the model to call it INSTEAD of dealing with the caller, and the goal here is
 * a counter, not a new conversational branch.
 */
export const NoteUnmetRequestArgs = z.object({
    kind: UnmetRequestKind.describe('Which kind of request this was. Use "no_availability" when they asked for a day or time and nothing was free, and "other" rather than forcing a bad fit.'),
    summary: z
        .string()
        .max(200)
        .describe('What the caller actually wanted, in one short line, in English.'),
});
export const NoteUnmetRequestResult = z.object({ ok: z.boolean() });
/**
 * `take_message` (doc 28) — the caller's problem, question, request or comment,
 * written down for a human to read.
 *
 * Recording only, like `note_unmet_request`, and for the same reason: a tool
 * that answered would tempt the model to call it INSTEAD of helping the caller.
 * The two are not alternatives — a message-ending call is still a call we could
 * not complete, so both get called (doc 28 D8).
 */
export const TakeMessageArgs = z.object({
    kind: CallerMessageKind.describe('What sort of thing this is. Use "comment" rather than forcing a bad fit.'),
    summary: z
        .string()
        .max(400)
        .describe("What the caller wants the business to know or do, in the BUSINESS's language (the owner reads this, not the caller). Write it as a note to a colleague — what the problem is, since when, and anything they said that would change what the business does about it. Never invent detail the caller did not give, and never write the caller's name or number here: they are recorded already."),
    callback_requested: z
        .boolean()
        .nullish()
        .describe('True ONLY if the caller actually asked to be called back.'),
});
/** `ok: true` ONLY once the message is durably stored (doc 32 F11). */
export const TakeMessageResult = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true) }),
    z.object({
        ok: z.literal(false),
        reason: z.enum(['invalid', 'save_failed', 'not_allowed']),
        retryable: z.boolean().optional(),
    }),
]);
export const CancelAppointmentArgs = z.object({
    appointment_id: z
        .string()
        .min(1)
        .describe("The id of one of the caller's own upcoming appointments, exactly as it was given to you."),
    reason: z.string().max(200).nullish().describe('Short free-text reason, only if the caller gave one.'),
});
export const RescheduleAppointmentArgs = z.object({
    appointment_id: z
        .string()
        .min(1)
        .describe("The id of one of the caller's own upcoming appointments, exactly as it was given to you."),
    new_start: z
        .string()
        .describe('New ISO 8601 start with the business timezone offset, e.g. 2026-07-24T10:30:00+02:00.'),
    service_id: z
        .string()
        .nullish()
        .describe('Only when the caller also wants a DIFFERENT service; omit to keep the same one.'),
    staff_id: z
        .string()
        .nullish()
        .describe('Only when the caller asked for a specific person; omit to keep the one they already have.'),
    service_answers: ServiceAnswersArg.describe('Only with a DIFFERENT service_id: the caller\'s answer to every question listed with that new service. Omit when the service stays the same — the appointment keeps its answers.'),
});
// ── Tool result shapes (returned to the model) ───────────────────────────────
export const FindAvailableSlotsResult = z.object({
    date: LocalDate,
    /** One PAGE of open start times for the date as "HH:mm" (local), at or after
     * `from_time` — at most `MAX_SLOTS_RETURNED` (48); `more`/`next_from` page the
     * rest. What may be booked, not what is said: `ranges` are said. */
    slots: z.array(z.string()),
    /**
     * The same starts collapsed into contiguous ranges, e.g. `["09:00-12:30",
     * "14:15-17:00"]` — what the assistant SAYS, while `slots` stays what it may
     * book. Computed here rather than left to the model: a free day can hold
     * hundreds of starts, and asked to summarise them it either drops some or
     * invents one. A range's end is the last bookable start, not a closing time.
     * Always the WHOLE day, whatever `from_time` and the page size (doc 56 C6).
     */
    ranges: z.array(z.string()),
    /**
     * How to SAY this date: the English weekday name, plus "today"/"tomorrow" when
     * it is one (doc 30 §4).
     *
     * Computed server-side because the model gets it wrong out loud. Asked to
     * verbalise 2026-09-30 in Ukrainian it said "the thirty-first of September"
     * — a date that does not exist — five times in one call, while booking the
     * correct ISO start. The caller and the diary walked away disagreeing.
     */
    weekday: z.string(),
    relative: z.enum(['today', 'tomorrow']).nullable(),
    /**
     * The whole date to SAY when the day is not today/tomorrow, or when the
     * caller asks for it: English "Monday, 12 October", read (translated, never
     * recalculated) into the caller's language. Added 2026-10-02 after a caller
     * asked three times which date "next Monday" was and the assistant refused,
     * then moved her appointment to an unnamed Monday (doc 37).
     */
    full_date: z.string().optional(),
    /** Calendar days from today (0 = today): "this Monday" vs "next Monday". */
    days_from_today: z.number().int().optional(),
    /** More bookable starts follow this page; ask again with `from_time` =
     * `next_from` to list them. `ranges` always cover the WHOLE day. */
    more: z.boolean(),
    next_from: z.string().nullable(),
    /** Total bookable starts on the day. */
    total: z.number().int(),
    /**
     * Set only when the date cannot be booked at all, whatever the diary says
     * (doc 56 E4): after the last bookable date, already past, or wholly inside
     * the business's minimum booking notice (doc 84). Empty ranges
     * then mean "not bookable", never "fully booked", and asking again will not
     * change the answer (`retryable: false`).
     */
    reason: z.enum(['outside_booking_window', 'past_date', 'too_soon']).optional(),
    /** The last bookable date (YYYY-MM-DD), with `reason: outside_booking_window`. */
    max_booking_date: LocalDate.optional(),
    /** With `reason: too_soon` (doc 84): nothing on the date can be booked
     * because of the business's minimum booking notice; this is the first date
     * the notice allows — it may still be closed or full. */
    earliest_date: LocalDate.optional(),
    retryable: z.boolean().optional(),
});
/** Every reason a booking can be refused, structural or workflow (doc 33 W6). */
export const BookingRefusal = z.enum([
    'slot_taken',
    'invalid',
    'closed',
    'past_notice',
    'unknown_service',
    'unknown_staff',
    /** After the last bookable date (doc 56 E4): a limit, not a full diary. */
    'outside_booking_window',
    /** Withheld caller ID and no callback number given (doc 32 F11). */
    'callback_number_required',
    /** No caller turn has followed the read-back of this exact version. */
    'confirmation_required',
    /** Not an intent this session prepared (a guessed or stale id). */
    'unknown_intent',
    /** The details were changed after this version was read back. */
    'stale_intent',
    /** The call is closing; no new changes are accepted. */
    'not_allowed',
    /** The booking may or may not have been saved (timeout). Never say it failed. */
    'outcome_unknown',
    /** A technical failure, not an answer about the business. */
    'internal_error',
    /** A question of the service is not answered yet (doc 64): ask the next one in
     * `missing_questions`. Nothing is wrong with the booking otherwise. */
    'service_answers_required',
    /** An answer names a question the service does not ask, repeats one, or is too long. */
    'invalid_service_answers',
    /** No name to book under (doc 65): ask the caller for their first name — nothing else is wrong. */
    'customer_name_required',
    /** The name cannot be stored (too long, not a name): ask again. */
    'invalid_customer_name',
]);
/** Which questions still need an answer, on an answers refusal (doc 64). */
const MissingQuestions = z
    .array(z.object({ question_id: z.string(), question: z.string() }))
    .max(MAX_SERVICE_QUESTIONS)
    .optional();
const InvalidQuestionIds = z.array(z.string()).max(MAX_SERVICE_QUESTIONS * 2).optional();
const InvalidFields = z.array(z.string()).max(10).optional();
/**
 * A `prepare_booking` call that did not parse, as an answer the model can act
 * on (doc 65 B2). A missing or blank name is `customer_name_required` — the
 * assistant asks for it and keeps everything else — and an unusable one is
 * `invalid_customer_name`; anything else is the generic `invalid`, naming the
 * arguments at fault. No submitted value is ever echoed back.
 */
export function bookingArgsRefusal(args, error) {
    const fields = [...new Set(error.issues.map((i) => String(i.path[0] ?? '(arguments)')))].slice(0, 10);
    if (fields.length === 1 && fields[0] === 'customer_name') {
        const raw = args && typeof args === 'object' ? args.customer_name : undefined;
        const check = checkCustomerName(raw);
        return { ok: false, reason: check.ok ? 'invalid_customer_name' : check.reason, field: 'customer_name' };
    }
    return { ok: false, reason: 'invalid', message: 'invalid_arguments', fields };
}
export const BookAppointmentResult = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        appointmentId: z.string(),
        /** Present when the business is staffed — the assigned person, for read-back. */
        staffName: z.string().optional(),
        /** True when this repeats an earlier commit of the same intent. */
        replayed: z.boolean().optional(),
    }),
    z.object({
        ok: z.literal(false),
        reason: BookingRefusal,
        message: z.string().optional(),
        alternatives: z.array(z.string()).optional(),
        /** The tool failed for a technical reason; it says nothing about the diary. */
        error: z.string().optional(),
        retryable: z.boolean().optional(),
        missing_questions: MissingQuestions,
        invalid_question_ids: InvalidQuestionIds,
        field: z.string().optional(),
        fields: InvalidFields,
    }),
]);
/** What `prepare_booking` returns: the read-back, in data, never prose. */
export const PrepareBookingResult = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        intent_id: z.string(),
        version: z.number().int(),
        read_back: z.object({
            service: z.string(),
            duration_min: z.number(),
            price: z.number(),
            /** Upper end when the service is priced as a range; `price` is then "from". */
            price_max: z.number().nullable().optional(),
            currency: z.string(),
            date: z.string(),
            weekday: z.string(),
            relative: z.enum(['today', 'tomorrow']).nullable(),
            full_date: z.string().optional(),
            days_from_today: z.number().int().optional(),
            time: z.string(),
            name: z.string(),
            staff: z.string().nullable(),
            callback_number: z.string().nullable(),
        }),
    }),
    z.object({
        ok: z.literal(false),
        reason: BookingRefusal,
        message: z.string().optional(),
        alternatives: z.array(z.string()).optional(),
        error: z.string().optional(),
        missing_questions: MissingQuestions,
        invalid_question_ids: InvalidQuestionIds,
        /** The one argument at fault, on a name refusal (never its value). */
        field: z.string().optional(),
        /** The arguments at fault, on a generic `invalid` (names only, never values). */
        fields: InvalidFields,
    }),
]);
/** Name-only by design (doc 13 D4): a spoken number is unverified, so history
 * and booking counts stay on the verified caller-ID path (doc 11 §6.1). */
export const LookupClientResult = z.discriminatedUnion('found', [
    z.object({ found: z.literal(true), name: z.string() }),
    z.object({ found: z.literal(false), error: z.enum(['invalid_arguments', 'lookup_failed']).optional() }),
]);
/** Results of changing an existing appointment (doc 14 §4). The failure
 * vocabulary is shared with the service layer (`ChangeFailureReason`). */
export const CancelAppointmentResult = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        /** Already cancelled ⇒ idempotent no-op, not an error to explain away. */
        alreadyCancelled: z.boolean(),
        serviceName: z.string(),
        start: z.string(),
    }),
    z.object({ ok: z.literal(false), reason: ChangeFailureReason }),
]);
export const RescheduleAppointmentResult = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        appointmentId: z.string(),
        start: z.string(),
        staffName: z.string().optional(),
    }),
    z.object({
        ok: z.literal(false),
        reason: ChangeFailureReason,
        message: z.string().optional(),
        alternatives: z.array(z.string()).optional(),
        missing_questions: MissingQuestions,
        invalid_question_ids: InvalidQuestionIds,
    }),
]);
// ── Tool names ───────────────────────────────────────────────────────────────
export const TOOL_NAMES = {
    findAvailableSlots: 'find_available_slots',
    prepareBooking: 'prepare_booking',
    bookAppointment: 'book_appointment',
    lookupClient: 'lookup_client',
    cancelAppointment: 'cancel_appointment',
    rescheduleAppointment: 'reschedule_appointment',
    noteUnmetRequest: 'note_unmet_request',
    takeMessage: 'take_message',
    endCall: 'end_call',
};
/**
 * Version of the tool contract the model is given (doc 33 W9 #6), recorded on
 * every call. Bump it with any change to a tool's name, arguments or meaning.
 */
export const TOOL_SCHEMA_VERSION = '2026-10-16.change.v7';
// ── OpenAI tool-definition generation ────────────────────────────────────────
function toParameters(schema) {
    const json = zodToJsonSchema(schema, { target: 'openAi', $refStrategy: 'none' });
    delete json.$schema;
    return json;
}
/** The `tools` array for the OpenAI Realtime `session.update` message (LLD §4.5). */
export function buildOpenAiTools() {
    return [
        {
            type: 'function',
            name: TOOL_NAMES.findAvailableSlots,
            description: 'Get the open appointment times for a service on a given date — any date up to the last bookable one, however far ahead. Your instructions may already list the free times for the coming days; use this for any day that list does not cover, and for a staff member whose times are not listed. A day missing from that list is NOT a full day. Never invent times. "ranges" always cover the whole day — say all of them; "slots" lists bookable starts, and when "more" is true further starts exist from "next_from" (ask again with from_time to list them). A result with "reason" means that date cannot be booked at all (outside_booking_window: after "max_booking_date"; past_date: already gone) — say that, not that it is full, and do not ask again. A result with an "error" field is a FAILURE, never "no availability".',
            parameters: toParameters(FindAvailableSlotsArgs),
        },
        {
            type: 'function',
            name: TOOL_NAMES.prepareBooking,
            description: 'Hand over the details of ONE appointment the caller wants, once the service, time, the answers to every question listed with that service, and (when needed) name and callback number are all known — BEFORE reading anything back. Books nothing. On service_answers_required, ask the caller the first question in missing_questions (one at a time) and call it again with all the answers. Returns intent_id and read_back: read exactly those details back to the caller and wait for their answer. If they correct anything, call prepare_booking again with the corrected details. For a SECOND appointment in the same call, call it again for that one.',
            parameters: toParameters(PrepareBookingArgs),
        },
        {
            type: 'function',
            name: TOOL_NAMES.bookAppointment,
            description: 'Book the appointment the caller has just said yes to, by the intent_id prepare_booking returned. Only in the turn right after their yes to your read-back. If it returns confirmation_required, read the details back once more and wait for their answer.',
            parameters: toParameters(BookAppointmentArgs),
        },
        {
            type: 'function',
            name: TOOL_NAMES.lookupClient,
            description: 'Check whether a phone number belongs to an existing client of this business. Returns their name when found. Call it once after the caller has confirmed or stated their number.',
            parameters: toParameters(LookupClientArgs),
        },
        {
            type: 'function',
            name: TOOL_NAMES.cancelAppointment,
            description: "Cancel one of the caller's OWN upcoming appointments, freeing the slot. Only ever pass an id from the caller's appointment list you were given — never a guessed one. Call it as soon as the caller has clearly asked to cancel an appointment you can single out (their only one, or the one they named); if they have several and did not say which, ask which one first. The application checks the caller's own words: ok means it is cancelled — never ask them to confirm a clear request. needs_clarification: nothing was cancelled; ask the ONE thing its \"missing\" names, then call again after their answer. not_requested: they held it back — nothing was cancelled. (Only in the older confirmation mode: confirmation_required with read_back — ask that once, then call again after their yes.) Say it is cancelled only after this returns ok.",
            parameters: toParameters(CancelAppointmentArgs),
        },
        {
            type: 'function',
            name: TOOL_NAMES.rescheduleAppointment,
            description: "Move one of the caller's OWN upcoming appointments to a new time (optionally a different service or staff member), in one step, once the caller has said which appointment and the new day and time (never pick a time for them). A move to a DIFFERENT service needs the caller's answers to that service's questions (service_answers); on service_answers_required ask the first of missing_questions. Check the new time is free first (the free-times list in your instructions, or find_available_slots for a day it does not cover). The application checks the caller's own words against your arguments: ok means it is moved — never ask them to confirm a clear request. needs_clarification: nothing was moved; ask the ONE thing its \"missing\" names (which appointment, new day, new time, service, …), then call again after their answer. not_requested: they held it back. (Only in the older confirmation mode: confirmation_required with read_back — read it back, then call again after their yes.) If it fails, the original appointment is untouched.",
            parameters: toParameters(RescheduleAppointmentArgs),
        },
        {
            type: 'function',
            name: TOOL_NAMES.noteUnmetRequest,
            description: "Record that the caller asked for something you cannot do — anything you had to redirect, decline, or could not answer. Call it ONCE per distinct request, at the moment it happens, and then carry on with the caller exactly as you would have. It is silent bookkeeping: it does not help the caller and you must never mention it, wait for it, or let it change what you say.",
            parameters: toParameters(NoteUnmetRequestArgs),
        },
        {
            type: 'function',
            name: TOOL_NAMES.takeMessage,
            description: "Write down what the caller wants the business to know or do, when it is not something you can settle yourself — a problem with something they bought or had done, a question you could not answer, a request the business must decide on, or anything they explicitly ask you to pass on. Call it ONCE per call, when you have heard the whole thing, and then carry on normally. If they are also booking, call it as well — the message is attached to the appointments booked on this call. Only when it returns ok may you tell the caller you have noted it down and that the team will see it; if it fails, say you could not note it down. NEVER promise that someone will call back, and never read the message back to them.",
            parameters: toParameters(TakeMessageArgs),
        },
        {
            type: 'function',
            name: TOOL_NAMES.endCall,
            description: 'Hang up the call. Call this only AFTER you have completely finished speaking your goodbye phrase — the line stays open until you call it.',
            parameters: toParameters(EndCallArgs),
        },
    ];
}
//# sourceMappingURL=index.js.map